/**
 * MFA recovery — the Tier-3 staff queue (design/MFA_RECOVERY_PLAN.md).
 *
 * In shadow mode EVERY Tier-2 chain lands here with the box worker's verdict:
 * liveness detail, both face scores at recovery's OWN threshold (never IDV's),
 * and the id_hash field match — plus `would_auto_complete`, which is the §6.1
 * calibration signal (when live mode would have been right every time, the
 * threshold has earned the flip).
 *
 *   Approve  the request becomes 'approved'; the BOUND session applies it on
 *            its next status poll (aal2 + factor reset). Staff never mint aal2
 *            into a session directly, and an approval only ever reaches the
 *            device that started the flow.
 *   Deny     the user stays locked to their existing factor.
 *
 * staff_reset (0457, founder 2026-10-04): a locked-out person with no second
 * device and no ID on file asked a person. There is no identity evidence;
 * the card shows what there is: their note, how old the account is, how it
 * signs in, when the lost factor was last used, and where recent sessions
 * came from, beside the request's own ip. Approving one is the same act as
 * the 2026-10-04 hand reset of @breezy: do it only when that picture fits.
 *
 * Two-step confirm on both (§0c). Remember what a wrong approve is: silent,
 * permanent account takeover — the exact attacker MFA exists to stop. When in
 * doubt, deny; the user can run the chain again.
 */

import { livenessDecidedLabel, livenessOrderLabel } from '../lib/liveness-labels';
import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { moderationApi, type MfaRecoveryRequest } from '../lib/moderationApi';
import { TabBar } from '../components/ui/TabBar';
import { PageShell } from '../components/ui/PageShell';

export const Route = createFileRoute('/mfa-recovery')({
  component: MfaRecoveryRoute,
});

function MfaRecoveryRoute() {
  return (
    <AdminShell mode="feature" featureName="MFA recovery">
      <RecoveryConsole />
    </AdminShell>
  );
}

type View = 'staff_review' | 'decided';

function RecoveryConsole() {
  const [view, setView] = useState<View>('staff_review');
  const [rows, setRows] = useState<MfaRecoveryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [arming, setArming] = useState<string | null>(null); // `${id}:${action}` two-step confirm
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (v: View) => {
    setLoading(true);
    setError(null);
    try {
      const r = await moderationApi.mfaRecovery(v);
      setRows(r.requests);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load(view);
  }, [view, load]);

  async function decide(id: string, action: 'approve' | 'deny') {
    const key = `${id}:${action}`;
    if (arming !== key) {
      setArming(key);
      return;
    }
    setArming(null);
    setBusy(id);
    try {
      await moderationApi.mfaRecoveryDecide(id, action);
      await load(view);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const legLine = (r: MfaRecoveryRequest) => {
    const v = r.verdict;
    if (!v) return 'no verdict recorded';
    if (v.cooldown) return 'COOLDOWN — chain not run (recent face/liveness failure)';
    const legs = v.legs ?? {};
    const bits = [
      `liveness: ${legs.liveness ? 'pass' : v.liveness_detail?.unavailable ? 'UNAVAILABLE' : 'FAIL'}`,
      livenessOrderLabel(v.liveness_detail?.sequence_match),
      livenessDecidedLabel(v.liveness_detail),
      `face vs reference selfie: ${
        v.face_reference?.missing ? 'no reference' : typeof v.face_reference?.score === 'number' ? `${v.face_reference.score.toFixed(3)} ${legs.face_reference ? 'pass' : 'FAIL'}` : v.face_reference?.error ?? '—'
      }`,
      `face vs ID portrait: ${
        typeof v.face_id?.score === 'number' ? `${v.face_id.score.toFixed(3)} ${legs.face_id ? 'pass' : 'FAIL'}` : v.face_id?.error ?? '—'
      }`,
      `fields (id_hash): ${legs.fields ? 'MATCH' : v.fields?.error ? v.fields.error : 'MISMATCH'}`,
      typeof v.face_threshold === 'number' ? `bar ${v.face_threshold}` : null,
    ].filter(Boolean);
    return bits.join(' · ');
  };

  return (
    <PageShell
      title="MFA recovery"
      description="Requests to reset a second factor. Approving one lets the account sign in without its enrolled factor, so the identity evidence has to carry the decision."
      maxWidth={860}
    >
      <TabBar
        label="MFA recovery queue"
        activeKey={view}
        onSelect={setView}
        items={[
          { key: 'staff_review', label: 'Needs a decision' },
          { key: 'decided', label: 'Decided' },
        ]}
      />
      {error ? (
        <p role="alert" className="mb-2 text-[13px]" style={{ color: 'var(--c-state-danger, #C7563E)' }}>
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>Loading…</p>
      ) : error ? null : rows.length === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>
          {view === 'staff_review' ? 'Nothing waiting for a decision.' : 'No decided requests yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((r) => (
            <div
              key={r.id}
              className="rounded p-3"
              style={{ border: '1px solid var(--c-border-hairline)', background: 'var(--c-bg-surface)', fontFamily: 'Inter', fontSize: 13 }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <b>@{r.handle}</b> ·{' '}
                  {r.kind === 'idv_reset'
                    ? `identity reset · attempt ${r.identity_attempts}`
                    : r.kind === 'staff_reset'
                      ? 'staff request (no second device)'
                      : r.kind === 'email_reset'
                        ? 'email reset (72h hold)'
                        : r.kind}
                </div>
                <div style={{ color: 'var(--c-fg-tertiary)', fontSize: 12 }}>{parseServerDate(r.created_at).toLocaleString()}</div>
              </div>
              {r.kind === 'idv_reset' ? (
                <div className="mt-1.5" style={{ color: 'var(--c-fg-secondary)' }}>{legLine(r)}</div>
              ) : null}
              {r.user_note ? (
                <blockquote
                  className="mt-1.5 pl-2.5"
                  style={{ borderLeft: '2px solid var(--c-border-strong)', color: 'var(--c-fg-default)', whiteSpace: 'pre-wrap' }}
                >
                  {r.user_note}
                </blockquote>
              ) : null}
              {r.kind !== 'idv_reset' && r.context ? <ContextBlock c={r.context} requestIp={r.ip} /> : null}
              {r.would_auto_complete != null ? (
                <div className="mt-1" style={{ fontSize: 12, color: r.would_auto_complete ? '#5C7A3D' : 'var(--c-fg-tertiary)' }}>
                  live mode would have: {r.would_auto_complete ? 'AUTO-APPROVED' : 'routed here anyway'}
                </div>
              ) : null}
              <div className="mt-1" style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                {[r.ip ? `ip ${r.ip}` : null, r.user_agent ? r.user_agent.slice(0, 80) : null, r.decision_note ?? null]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {r.state !== 'staff_review' ? (
                <div className="mt-1.5" style={{ fontSize: 12, color: 'var(--c-fg-secondary)' }}>
                  <b style={{ color: r.state === 'denied' ? '#9A4230' : '#5C7A3D' }}>{r.state}</b> by {r.decided_by ?? '—'}
                  {r.decided_at ? ` · ${parseServerDate(r.decided_at).toLocaleString()}` : ''}
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  {(['approve', 'deny'] as const).map((a) => (
                    <button
                      key={a}
                      disabled={busy === r.id}
                      onClick={() => void decide(r.id, a)}
                      className="rounded px-3 py-1.5 text-[13px]"
                      style={{
                        background: arming === `${r.id}:${a}` ? (a === 'approve' ? '#5C7A3D' : '#9A4230') : 'var(--c-bg-surface-alt)',
                        color: arming === `${r.id}:${a}` ? '#FAF7F2' : 'var(--c-fg-default)',
                        border: '1px solid var(--c-border-hairline)',
                        opacity: busy === r.id ? 0.6 : 1,
                      }}
                    >
                      {arming === `${r.id}:${a}` ? `Click again to ${a}` : a === 'approve' ? 'Approve reset' : 'Deny'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}

/** The account's picture for a request with no identity evidence (0457). */
function ContextBlock({ c, requestIp }: { c: NonNullable<MfaRecoveryRequest['context']>; requestIp: string | null }) {
  const when = (v: string | null | undefined) => (v ? parseServerDate(v).toLocaleString() : 'never');
  const line = { fontSize: 12, color: 'var(--c-fg-secondary)' } as const;
  return (
    <div className="mt-1.5 flex flex-col gap-0.5" style={line}>
      <div>
        account since {c.account_created ? parseServerDate(c.account_created).toLocaleDateString() : '?'}
        {c.signup_country ? ` · signed up from ${c.signup_country}` : ''} · password {c.has_password ? 'yes' : 'no'}
      </div>
      <div>sign-in: {(c.sign_in ?? []).map((i) => `${i.provider} (last ${when(i.last)})`).join(', ') || 'password only'}</div>
      <div>
        lost factor last used:{' '}
        {[
          ...(c.factors ?? []).map((f) => `${f.type} ${when(f.last)}`),
          ...(c.passkeys ?? []).map((k) => `passkey "${k.name ?? ''}" ${when(k.last)}`),
        ].join(', ') || 'none on file'}
      </div>
      <div>
        recent sessions (request ip {requestIp ?? '?'}):
        <ul className="ml-4 list-disc">
          {(c.sessions ?? []).map((s, i) => (
            <li key={i} style={{ fontWeight: s.ip && s.ip === requestIp ? 600 : 400 }}>
              {when(s.updated)} · {s.aal ?? '?'} · {s.ip ?? '?'} · {s.ua ?? ''}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
